import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { COHORTS_PATH } from '@verantos-dev/core-constants';
import type { CohortBuilderDefinitionLegacy } from '@verantos-dev/types-cohorts';

import { authFetch } from './fetchers';
import { parseOrFallback } from './parseOrFallback';
import { cohortQueryKeys } from './queryKeys';
import { objectResponse } from './schemas';

/**
 * Tolerant zod schema for the cohort-version payload. We keep `jsonValue`
 * loosely typed (`z.unknown()`) because validating the full DSL shape here
 * would couple the data-access layer to the DSL schema; consumers re-parse.
 */
const cohortVersionSchema = z
  .object({
    cohortVersionId: z.string(),
    cohortProfileId: z.string().optional(),
    dslVersion: z.string().optional(),
    sqlValue: z.string().optional(),
    jsonValue: z.unknown().optional(),
  })
  .passthrough();

export interface CohortVersionView {
  cohortVersionId: string;
  cohortProfileId: string;
  dslVersion: string;
  sqlValue: string;
  jsonValue?: CohortBuilderDefinitionLegacy;
}

interface UseCohortVersionQueryParams {
  projectId: string;
  cohortId: string;
  cohortVersionId: string;
  /** Force-disable the query (default: enabled when all three ids are set). */
  enabled?: boolean;
}

/**
 * Shared fetcher used by both `useCohortVersionQuery` and imperative
 * `queryClient.prefetchQuery` callsites (e.g. row-click priming). Lives at
 * module scope so the prefetch path and the hook path stay in lock-step.
 */
export const fetchCohortVersion = async (
  projectId: string,
  cohortId: string,
  cohortVersionId: string
): Promise<CohortVersionView> => {
  const raw = await authFetch<unknown>(
    `/${COHORTS_PATH}/${projectId}/cohort-profile/${cohortId}/cohort-version/${cohortVersionId}`
  );
  const envelope = parseOrFallback(
    objectResponse(cohortVersionSchema),
    raw,
    'useCohortVersionQuery'
  );
  const parsed = envelope.data;
  return {
    cohortVersionId: parsed.cohortVersionId,
    cohortProfileId: parsed.cohortProfileId ?? '',
    dslVersion: parsed.dslVersion ?? '',
    sqlValue: parsed.sqlValue ?? '',
    jsonValue: parsed.jsonValue as CohortBuilderDefinitionLegacy | undefined,
  };
};

/**
 * Canonical TanStack read for the cohort-version endpoint
 * (`/cohorts/<projectId>/cohort-profile/<cohortId>/cohort-version/<versionId>`).
 *
 * Keyed on `cohortVersionId` — same cache entry across consumers that need
 * the same version. Replaces the RTK `useGetCohortVersionQuery` endpoint
 * from `cohortAPISlice` for any consumer that needs the projected
 * `CohortVersionView` shape.
 */
export const useCohortVersionQuery = ({
  projectId,
  cohortId,
  cohortVersionId,
  enabled,
}: UseCohortVersionQueryParams) => {
  return useQuery<CohortVersionView>({
    queryKey: cohortQueryKeys.version(cohortVersionId),
    queryFn: () => fetchCohortVersion(projectId, cohortId, cohortVersionId),
    enabled: enabled !== false && !!projectId && !!cohortId && !!cohortVersionId,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });
};
